import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { cloneDeep } from 'lodash-es';
import {
  bpNavigation,
  compactNavigation,
  defaultNavigation,
  futuristicNavigation,
  horizontalNavigation,
} from '../navigation/data';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
  private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
  private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
  private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
  private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;
  private readonly _bpNavigation: FuseNavigationItem[] = bpNavigation;

  /**
   * Constructor
   */
  constructor(private _fuseMockApiService: FuseMockApiService) {
    // Register Mock API handlers
    this.registerHandlers();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Register Mock API handlers
   */
  registerHandlers(): void {
    // -----------------------------------------------------------------------------------------------------
    // @ Navigation - GET
    // -----------------------------------------------------------------------------------------------------
    this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
      // Fill compact navigation children using the default navigation
      this._compactNavigation.forEach(compactNavItem => {
        this._defaultNavigation.forEach(defaultNavItem => {
          if (defaultNavItem.id === compactNavItem.id) {
            compactNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Fill futuristic navigation children using the default navigation
      this._futuristicNavigation.forEach(futuristicNavItem => {
        this._defaultNavigation.forEach(defaultNavItem => {
          if (defaultNavItem.id === futuristicNavItem.id) {
            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Fill horizontal navigation children using the default navigation
      this._horizontalNavigation.forEach(horizontalNavItem => {
        this._defaultNavigation.forEach(defaultNavItem => {
          if (defaultNavItem.id === horizontalNavItem.id) {
            horizontalNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Return the response
      return [
        200,
        {
          compact: cloneDeep(this._compactNavigation),
          default: cloneDeep(this._defaultNavigation),
          futuristic: cloneDeep(this._futuristicNavigation),
          horizontal: cloneDeep(this._horizontalNavigation),
          bp: this.sortSubmenus(cloneDeep(this._bpNavigation)),
        },
      ];
    });
  }

  /**
   * Ordena recursivamente los elementos de navegación alfabéticamente por título
   */
  private sortNavigationItems(items: FuseNavigationItem[]): FuseNavigationItem[] {
    return items
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      .map(item => ({
        ...item,
        children: item.children ? this.sortNavigationItems(item.children) : undefined,
      }));
  }

  /**
   * Ordena recursivamente solo los submenús alfabéticamente por título.
   * Los ítems con `meta.order` (número) se anclan primero según ese valor;
   * el resto conserva el orden alfabético habitual.
   */
  private sortSubmenus(items: FuseNavigationItem[]): FuseNavigationItem[] {
    return items.map(item => ({
      ...item,
      children: item.children
        ? (() => {
            const sorted = this.sortSubmenus(item.children);
            return sorted.sort((a, b) => {
              const orderA = typeof a.meta?.order === 'number' ? a.meta.order : Number.MAX_SAFE_INTEGER;
              const orderB = typeof b.meta?.order === 'number' ? b.meta.order : Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              return (a.title || '').localeCompare(b.title || '');
            });
          })()
        : undefined,
    }));
  }
}
